import { useQuery } from '@tanstack/react-query';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import { apiGet } from '../lib/api';
import { getApiBase } from '../lib/submissions';
import useCampaignVersion from './useCampaignVersion';

export interface DonationForm {
  label?: string | null;
  embedScript?: string | null;
}

export interface CalendarEmbed {
  embedUrl?: string | null;
  description?: string | null;
}

export interface PerkItem {
  body?: string | BlocksContent | null;
  embed?: string | null;
  isActive?: boolean | null;
}

export interface EligibilityItem {
  id?: number | string;
  iconName?: string | null;
  heading?: string | null;
  body?: string | BlocksContent | null;
}

export interface CampaignAsset {
  id?: number | string;
  url?: string | null;
  alternativeText?: string | null;
  data?: unknown;
  attributes?: unknown;
}

export interface ScheduleSection {
  header?: string | null;
  subhead?: string | null;
  body?: string | BlocksContent | null;
  calendar?: CalendarEmbed | null;
}

export interface MatchingSection {
  header?: string | null;
  subhead?: string | null;
  body?: string | BlocksContent | null;
  forms?: DonationForm[] | null;
}

export interface NominateSection {
  header?: string | null;
  subhead?: string | null;
  body?: string | BlocksContent | null;
  form_header?: string | null;
  form_enabled?: boolean;
  assets?: CampaignAsset[] | null;
  eligibility_items?: EligibilityItem[] | null;
  video_submit?: {
    header?: string | null;
    body?: string | BlocksContent | null;
  } | null;
  graphic_assets?: {
    title?: string | null;
    body?: string | BlocksContent | null;
    images?: CampaignAsset[] | null;
  } | null;
}

export interface PerksBlock {
  header?: string | null;
  subhead?: string | null;
  cta_enabled?: boolean;
  cta_text?: string | null;
  cta_url?: string | null;
  items?: PerkItem[] | null;
  isActive?: boolean | null;
  // Shows the live Tiltify perk grid. Independent of `items`, so the grid and the
  // Airtable embed can each be switched on or off.
  live_perks_enabled?: boolean | null;
}

export interface ActiveCampaign {
  id: number;
  documentId: string;
  Title?: string | null;
  Active?: boolean;
  Year?: string | null;
  // Always the single theme the backend resolved (or null) — never the array.
  // Carries its own key/name/isDark, so the identity is not duplicated at the
  // top level.
  designTokens?: unknown;
  // Identifier + label for every configured theme — enough to populate a future
  // palette selector. The tokens themselves arrive via resolved designTokens,
  // so this deliberately carries no token fields. Nothing reads it today.
  themes?: Array<{ key: string | null; name: string | null }>;

  scheduleSection?: ScheduleSection | null;
  matchingSection?: MatchingSection | null;
  nominateSection?: NominateSection | null;
  perksBlock?: PerksBlock | null;
}

async function fetchActiveCampaign(version: string): Promise<ActiveCampaign> {
  const res = await apiGet(`${getApiBase()}/active-campaign?v=${version}`);
  if (!res.ok) throw new Error(`Failed to fetch active campaign: ${res.statusText}`);
  return res.json();
}

export function useActiveCampaign() {
  // Cache-buster: a campaign save can't reach an already-cached browser/CDN
  // response for the old URL, so a version bump (this poller, always no-store)
  // forces a brand-new URL and a guaranteed cache miss. Unchanged requests
  // keep hitting the existing 15min server/CDN cache exactly as before.
  const { version, error: versionError, refetch: refetchVersion } = useCampaignVersion();

  const query = useQuery<ActiveCampaign, Error>({
    queryKey: ['active-campaign-entity', version],
    queryFn: () => fetchActiveCampaign(version as string),
    enabled: version != null,
    // Matches the staleTime/gcTime the 4 pages + ActiveYearCampaign.tsx each
    // independently declared pre-P4A-158 — content here changes rarely.
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // A version fetch that keeps failing must surface as an error, not hang
  // isLoading forever — the version poller retries on its own interval and
  // self-heals; this only fires while it's still down.
  const versionPending = version == null && versionError == null;
  const versionUnavailable = version == null && versionError != null;

  return {
    ...query,
    isLoading: versionPending || query.isLoading,
    isError: query.isError || versionUnavailable,
    error: query.error ?? versionError,
    // versionUnavailable means the entity query never ran (gated by `enabled`),
    // so retrying it would refetch nothing new — retry the version poll instead.
    refetch: versionUnavailable ? refetchVersion : query.refetch,
  };
}
